// =====================================================================================
// INVRT Radar — web console — one site
//
// The shell for a single site: the header (domain, pixel state, trial, budget) and the
// tab registration. Every panel lives in its own file under ./site/ (C1 split), and the
// active tab is URL-driven (`?tab=`) so a link elsewhere in the console can land on the
// right one.
//
// Tab ids, in display order: visitors, install, plan, icp, known, excluded, consent. All
// seven are built (the C1 wave, 21 Aug 2026, added plan and excluded and rebuilt consent).
//
// The honest bit throughout: person-level identification is a United States capability of
// the underlying data source. Everywhere else a visit resolves at COMPANY level, and this
// page says so rather than implying a named individual it cannot produce.
// =====================================================================================

import { Link, useParams, useSearchParams } from "react-router-dom";
import { trialLabel } from "@/lib/trialLabel";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Tabs } from "@/components/ui/tabs";
import type { Site } from "@/lib/types";
import { VisitorsPanel } from "./site/VisitorsPanel";
import { InstallPanel } from "./site/InstallPanel";
import { PlanPanel } from "./site/PlanPanel";
import { IcpPanel } from "./site/IcpPanel";
import { KnownCustomersPanel } from "./site/KnownCustomersPanel";
import { ExcludedIpsPanel } from "./site/ExcludedIpsPanel";
import { ConsentPanel } from "./site/ConsentPanel";

export function SiteDetailPage() {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const api = useApiClient();
  const { orgId } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? "visitors";

  const site = useQuery({
    queryKey: ["site", orgId, siteId],
    queryFn: () => api.get<Site>(`/sites/${siteId}`),
    enabled: Boolean(orgId) && siteId !== "",
  });

  if (site.isLoading) return <LoadingState label="Loading this site…" />;
  if (site.isError) return <ErrorState error={site.error} onRetry={() => void site.refetch()} />;
  if (!site.data) return null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to="/sites"
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          All sites
        </Link>
        <h1 className="mt-2 text-xl font-semibold text-ink">{site.data.domain}</h1>
        <div className="mt-2 flex flex-wrap gap-2">
          <Badge tone={site.data.pixel_status === "verified" ? "success" : "neutral"}>
            {site.data.pixel_status === "verified"
              ? "Verified — visitors arriving"
              : site.data.pixel_status === "installed"
                ? "Pixel found on the page"
                : "Pixel not detected yet"}
          </Badge>
          {site.data.trial_state ? (
            <Badge tone="info">{trialLabel(site.data.trial_state)}</Badge>
          ) : null}
          {site.data.budget_ids_month !== null ? (
            <Badge tone="neutral">
              Up to {site.data.budget_ids_month.toLocaleString("en-GB")} identifications a month
            </Badge>
          ) : null}
        </div>
      </div>

      <Tabs
        activeId={tab}
        onChange={(next) => setSearchParams({ tab: next })}
        items={[
          { id: "visitors", label: "Visitors", content: <VisitorsPanel siteId={siteId} /> },
          { id: "install", label: "Install", content: <InstallPanel site={site.data} /> },
          { id: "plan", label: "Plan", content: <PlanPanel site={site.data} /> },
          { id: "icp", label: "Ideal visitors", content: <IcpPanel site={site.data} /> },
          {
            id: "known",
            label: "Known customers",
            content: <KnownCustomersPanel site={site.data} />,
          },
          { id: "excluded", label: "Excluded IPs", content: <ExcludedIpsPanel site={site.data} /> },
          { id: "consent", label: "Consent", content: <ConsentPanel site={site.data} /> },
        ]}
      />
    </div>
  );
}
